import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    OnInit,
    Signal,
    signal,
    WritableSignal
} from "@angular/core";
import {LoadingStatus} from '../../session/loading-status';
import {LoadingService} from "../../ui-elements/loading/loading.service";
import {ResponsiveDirective} from "../../responsive/responsive.directive";
import {Page} from "../../navigation/page";
import {OrderingContext} from '../../ordering-context/ordering-context';
import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {NotificationService} from "../../notification/notification.service";
import {PromoCardComponent} from '../../promo/promo-card/promo-card.component';
import {PromoModel} from '../../promo/promo.model';
import {IdentificationMode} from '../../identity/identification-page/identification-mode';
import {IdentityModel} from '../../identity/identity.model';
import {AppPromoAction, AppPromoData} from '../../identity/member-summary-entity.model';
import {DealModel, DealResult, DealResultCode} from "../deal.model";
import {MenuItemData} from '../../menu/menu-entity.model';
import {DealData} from "../deal-entity.model";
import {TranslateModule} from '@ngx-translate/core';
import {ModalPageLayoutComponent} from '../../page-layout/modal-page-layout/modal-page-layout.component';
import {ButtonComponent} from '../../buttons/button/button.component';
import {TouchableDirective} from '../../ui-elements/touchable.directive';
import {ElementWidthDirective} from '../../utils/element-width.directive';
import {PageFooterControlDirective} from '../../page-layout/page-footer-control.directive';
import {ConfigurationModel} from '../../configuration/configuration.model';

import {MenuItemSelection} from '../../menu/menu.model';

/**
 * List of all available deals / promotions
 */
@Component({
    selector: "woa-deals-page",
    templateUrl: "./deals-page.component.html",
    styleUrl: "./deals-page.component.scss",
    providers: [{provide: Page, useExisting: DealsPageComponent}],
    imports: [
        ButtonComponent,
        ElementWidthDirective,
        ModalPageLayoutComponent,
        PageFooterControlDirective,
        PromoCardComponent,
        ResponsiveDirective,
        TranslateModule,
        TouchableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DealsPageComponent extends Page implements OnInit {
    private orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public identityModel: IdentityModel = inject(IdentityModel);
    public dealModel: DealModel = inject(DealModel);
    private promoModel: PromoModel = inject(PromoModel);
    private loadingService: LoadingService = inject(LoadingService);
    private notificationService: NotificationService = inject(NotificationService);

    LoadingStatus = LoadingStatus;

    public status: WritableSignal<LoadingStatus> = signal<LoadingStatus>(LoadingStatus.LOADING);

    public readonly allAvailableDeals: Signal<DealData[]> = computed<DealData[]>(() =>
        this.identityModel.currentMember().deals.filter((deal: DealData) =>
            !deal.isTicket && deal.isAvailableForOrderType(this.orderingContextModel.orderType())));

    public readonly memberDeals: Signal<DealData[]> = computed<DealData[]>(() =>
        this.allAvailableDeals().filter((deal: DealData) =>
            !deal.isTicket && this.configurationModel.isConsumerActor() && deal.memberDeal));

    public readonly appDeals: Signal<DealData[]> = computed<DealData[]>(() =>
        this.allAvailableDeals().filter((deal: DealData) =>
            !deal.isTicket && (this.configurationModel.isWaiterActor() || !deal.memberDeal)));

    public readonly allUntriggeredCardPromos: Signal<AppPromoData[]> = computed<AppPromoData[]>(() =>
        this.identityModel.currentMember().promos.filter((promo: AppPromoData) =>
            promo.isAvailableForOrderType(this.orderingContextModel.orderType()) &&
            !promo.triggered && promo.showAppPromoCard &&
            (!promo.promotedSellables.length || this.promoModel.canPromoteSellablesAsCard()(promo, false))));

    public readonly nrDeals: Signal<number> = computed<number>(() =>
        this.allAvailableDeals().length + (this.configurationModel.isConsumerActor() ? this.allUntriggeredCardPromos().length : 0));

    ngOnInit(): void {
        this.status.set(LoadingStatus.LOADING);
        this.loadMemberSummary();
    }

    loadMemberSummary(): void {
        void this.loadingService.performWithSpinner(async () => {
            try {
                await this.identityModel.loadMemberSummary();
                this.status.set(LoadingStatus.LOADED);
            } catch (error) {
                this.status.set(LoadingStatus.FAILED);
            }
        });
    }

    async enterDealCode(): Promise<void> {
        const result: DealResult = await this.navigationService.createPage("woa-deal-code-entry-page");
        if (result && (result.resultCode === DealResultCode.IMMEDIATE || result.resultCode === DealResultCode.DEFERRED)) {
            void this.dismiss();
        }
    }

    async login(): Promise<void> {
        return this.navigationService.createPage("woa-identification-page", {"mode": IdentificationMode.LOGIN});
    }

    async orderDeal(deal: DealData): Promise<void> {
        const result: DealResult = await this.dealModel.orderDeal(deal);
        if (result.resultCode === DealResultCode.UNAVAILABLE) {
            await this.notificationService.displayAlert(null, result.msg);
        } else if (result.resultCode === DealResultCode.IMMEDIATE || result.resultCode === DealResultCode.DEFERRED) {
            void this.dismiss();
        }
    }

    async promoActioned(promo: AppPromoData, menuItem: MenuItemData): Promise<void> {
        if (promo.promoCardAction === AppPromoAction.ORDER_PRODUCT && await this.orderingContextModel.applyOrderingContext(new OrderingContext().withSelectedMenuItem(new MenuItemSelection(menuItem)))) {
            void this.dismiss();
        } else if (promo.promoCardAction === AppPromoAction.SHOW_PRODUCTS) {
            this.promoModel.activatedPromo.set(promo);
            void this.dismiss();
        }
    }
}
